/* eslint-disable react/no-array-index-key */
import { IReview } from "api/interfaces/home/home.type";
import classNames from "classnames";
import StarRatings from "component/design-system/StarRatings";
import Button from "component/design-system/buttons/Button";
import Slider from "component/design-system/carousel/Slider";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import ReadMore from "component/read-more/ReadMore";
import { READ_ALL_REVIEWS_URL } from "constant/shared-constants";
import useScreenSize from "hoc/useScreenSize";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import HideIf from "util/HideIf";
import { dateToYMD } from "util/date-helper";
import GoogleRatingSummary from "./GoogleRatingSummary";
import styles from "./customer-review-v2.module.scss";

const INITIAL_COLORS = [
  "#3b82f6",
  "#a855f7",
  "#14b8a6",
  "#ef4444",
  "#f97316",
  "#f59e0b",
  "#84cc16",
  "#22c55e",
  "#06b6d4",
  "#ec4899",
];

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  return parts.map((p) => p[0]?.toUpperCase()).join("").slice(0, 2);
}

function ReviewCard({ review, colorIndex }: { review: IReview; colorIndex: number }) {
  const initials = getInitials(review.name);
  const color = INITIAL_COLORS[colorIndex];

  return (
    <div className={styles.review_card}>
      <div className={styles.review_card_left}>
        <div className={styles.initials_circle} style={{ backgroundColor: color }}>
          {initials}
        </div>
        <div className={styles.review_info}>
          <PAText component="span" bold>{review.name}</PAText>
          <div className="d-flex justify-content-between">
            <PAText component="span" fontSize={FONT_SIZE[6]} color="light-grey">
              {dateToYMD(new Date(review.updateTime))}
            </PAText>
            <StarRatings rating={review.rating} className={styles.star_rating} />
          </div>
          <ReadMore
            text={(
              <PAText
                component="span"
                fontSize={FONT_SIZE[1]}
                color="medium-grey"
              >
                {`"${review.review}"`}
              </PAText>
            )}
            lineClamp={3}
          />
        </div>
      </div>
    </div>
  );
}

function ReviewCardSlide({ review, colorIndex }: { review: IReview; colorIndex: number }) {
  const initials = getInitials(review.name);
  const color = INITIAL_COLORS[colorIndex];

  return (
    <div className={styles.review_card_slide}>
      <div className={styles.review_card_header}>
        <div className={styles.initials_circle} style={{ backgroundColor: color }}>
          {initials}
        </div>
        <div className={styles.review_meta}>
          <PAText component="span" bold>{review.name}</PAText>
          <PAText component="span" fontSize={FONT_SIZE[6]} color="light-grey">
            {dateToYMD(new Date(review.updateTime))}
          </PAText>
          <StarRatings rating={review.rating} className={styles.star_rating} />
        </div>
      </div>
      <ReadMore
        text={(
          <PAText
            component="span"
            fontSize={FONT_SIZE[1]}
            color="medium-grey"
          >
            {`"${review.review}"`}
          </PAText>
        )}
        lineClamp={3}
      />
    </div>
  );
}

interface ICustomerReviewV2Props {
  reviews: IReview[];
}

function CustomerReviewV2(props: ICustomerReviewV2Props) {
  const { reviews } = props;
  const { t } = useTranslation("home");
  const [, , isDesktop] = useScreenSize();

  if (!reviews || !reviews.length) {
    return null;
  }

  const reviewsToShow = isDesktop ? reviews : reviews.slice(0, 3);

  return (
    <div
      className={classNames(styles.customer_review_v2_container, {
        [styles.customer_review_v2_container_desktop]: isDesktop,
      })}
    >
      <PAText
        bold
        component="h2"
        className="text-center m-b-15 m-t-0"
        fontSize={FONT_SIZE[5]}
      >
        {t("Customers Love Parts Avatar")}
      </PAText>
      <PAText component="p" fontSize={FONT_SIZE[1]} color="medium-grey" className={styles.customer_review_v2_subtext}>
        {t("Join over 400,000+ verified customers")}
      </PAText>

      <GoogleRatingSummary />

      <HideIf show={!isDesktop}>
        <div className={styles.reviews_list}>
          {reviewsToShow.map((review, index) => (
            <ReviewCard key={review.reviewId} review={review} colorIndex={index} />
          ))}
        </div>
      </HideIf>
      <HideIf show={isDesktop}>
        <div className={styles.reviews_slider_wrapper}>
          <Slider
            tileWidth={400}
            ulClass={styles.reviews_slider_ul}
            showScrollOnHover
          >
            {reviewsToShow.map((review, index) => (
              <ReviewCardSlide key={review.reviewId} review={review} colorIndex={index} />
            ))}
          </Slider>
        </div>
      </HideIf>

      <div className={styles.read_all_container}>
        <Link
          href={READ_ALL_REVIEWS_URL}
          target="_blank"
        >
          <Button>
            {t("Read All Reviews")}
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default CustomerReviewV2;
